<script setup>
import { defineProps, onMounted, ref, useSlots } from 'vue'
import { computed } from '@vue/reactivity';

import Button from './Button.vue';

const props = defineProps({
  tool: String,
  showOnClick: Boolean,
  allowForLoop: Boolean,
  disabled: Boolean,
  minWidthFull: Boolean,
  menuXSide: {
    //left, right, null=auto
    type: String,
    default: null
  },
  menuYStart: {
    //top, bottom, null=auto
    type: String,
    default: null
  },
  childBaseColourClasses: String,
  dark: Boolean,
})


// Determine what, out of the slots, are menu items
const $slots = useSlots();

const computedChildren = computed(() => {
  return props.allowForLoop
    ? $slots.default()[0].children
    : $slots.default();
});


// Determine visibility and positioning
const shownFromClick = ref(false);
const menuOuterElement = ref();
const hoverableElement = ref();
const scrollableParent = ref(window)


onMounted(() => {
  // Find the nearest parent with an overflow class, if any exists, to make sure we're visible within it.
  function findScrollParent(node) {
    if (node == null) {
      return null;
    }

    if (node.className?.includes('overflow')) {
      return node;
    } else {
      return findScrollParent(node.parentNode);
    }
  }
  let scrollableParentOrNull = findScrollParent(menuOuterElement.value)
  if (scrollableParentOrNull) {
    scrollableParent.value = scrollableParentOrNull
  }

})

// Determine positioning, relative to the hoverable
const xSide = ref(props.menuXSide ?? 'left') // ALIGN the menu's right or left edge WITH the hoverable. 'left', 'right', 'middle'
const ySide = ref(props.menuYStart ?? 'bottom') // ABUT the menu's top or bottom edge TO the hoverable. 'top', 'bottom', 'middle'
// These are different because the menu prefers to be above or below, so it has to be pushed off of the hoverable in one direction or the other.

// If positioning is auto, check if we need to move the menu to be more visibile.
const hoverPositionTest = () => {
  if (props.menuXSide === null && props.menuYStart === null) {
    return;
  }
  setTimeout(() => {
    // We use the hoverable and parent positions, but ignore menu position and just use its size.
    // This way the menu's position changing doesn't affect this calculation and it shouldn't jitter.
    let parentBounds;
    if (!scrollableParent?.value?.getBoundingClientRect){
        parentBounds = document.body.getBoundingClientRect()
    } else {
        parentBounds = scrollableParent?.value?.getBoundingClientRect()
    }
    let hoverableBounds = hoverableElement.value.getBoundingClientRect()

    let menuBounds = menuOuterElement.value.getBoundingClientRect()

    // Determine auto behaviour when a side isn't set
    // Try to keep the menu visible near window edges.
    if (props.menuXSide === null) {

      // If either side will go outside the parent bounds, align center.
      if (
        (hoverableBounds.left + menuBounds.width) > parentBounds.right
        && (hoverableBounds.right - menuBounds.width) < parentBounds.left
      ) {
        xSide.value = 'middle'

      } else if ((hoverableBounds.left + menuBounds.width) > (parentBounds.right)) {
        // the menu's right edge is outside of the parent bounds, so align the right edge to shift it left.
        xSide.value = 'right';

      } else {
        xSide.value = 'left'; // default

      }
    }

    if (props.menuYStart === null) {

      // If either side will go outside the parent bounds, align center.
      if (
        (hoverableBounds.bottom + menuBounds.height) > parentBounds.bottom
        && (hoverableBounds.top - menuBounds.height) < parentBounds.top
      ) {
        ySide.value = 'middle'

      } else if ((hoverableBounds.bottom + menuBounds.height) > parentBounds.bottom) {
        // If the bottom of the menu is off the bottom of the parent
        ySide.value = 'top';

      } else {
        ySide.value = 'bottom';
      }
    }
  }, 1) // Skip a frame so that the menu has a chance to be shown, if this is the hover event that triggers showing it
}

const ignoreHover = (e) => {
  // We only care about hover events on the outer menu element; ignore those inside.
  e.stopPropagation();
}

const toggleShownFromClick = (e) => {
  if (props.showOnClick) {
    shownFromClick.value = !shownFromClick.value

    if (shownFromClick.value) {
      hoverPositionTest(e)
    }
  }
}
const xPositionClasses = computed(()=>{
  if (xSide.value == 'middle'){
    // Whatever the other settings, we're centered on the hoverable.
    return 'left-1/2 -translate-x-1/2'
  }
  if (ySide.value == 'middle'){
    // Offset so that we're not covering the hoverable
    return {
      'left': 'left-full',
      'right': 'right-full',
    }[xSide.value]
  }
  // Align with the left or right side of the hoverable.
  return {
    'left': 'left-0',
    'right': 'right-0',
  }[xSide.value]
})

const yPositionClasses = computed(()=>{
  return {
    'top': 'bottom-[90%]',
    'bottom': 'top-[90%]',
    'middle': 'top-1/2 -translate-y-1/2'
  }[ySide.value]
});


const computedChildColourClasses = computed(() =>
  'block px-4 py-2 w-full whitespace-nowrap '
  + 'bg-tool-'+props.tool+' hover:bg-tool-'+props.tool+'/75 text-white font-display '
  + props.childBaseColourClasses
)

</script>
<template>
  <div
    class="relative inline-block align-middle cursor-pointer"
    :class="{ 'group/hiddenmenu': !props.showOnClick }"
    @mouseover="hoverPositionTest"
  >
    <div ref="hoverableElement">
      <slot
        name="hoverable"
        :toggleShownFromClick="()=>{if(!props.disabled){toggleShownFromClick();}}"
      >
        <Button
          primary
          large
          compact
          text-only
          icon="menu"
          :disabled="props.disabled"
          :group-hover="props.showOnClick ? null : 'hiddenmenu'"
          @click="()=>{if(!props.disabled){toggleShownFromClick();}}"
        />
      </slot>
    </div>

    <div
      v-if="!props.disabled"
      :class="[
        'hidden absolute z-50 text-left text-sm py-1',
        xPositionClasses,
        yPositionClasses,
        props.showOnClick ? '' : 'group-hover/hiddenmenu:block',
        shownFromClick ? 'block!' : '',
        props.minWidthFull ? 'min-w-full' : 'min-w-48',
      ]"
      ref="menuOuterElement"
    >
      <template v-if="false">The outer div adds some space under the button, while allowing the
        hover state to continue smoothly into the menu, because it's on top of
        the original button.</template>

      <div
        class="
        rounded overflow-x-hidden overflow-y-auto max-h-[33vh] border drop-shadow-xl w-full
        dark:bg-slate-800 dark:border-slate-700 bg-white border-slate-200"
        @mouseover="ignoreHover"
        @click="() => { shownFromClick = false }"
      >
        <template v-if="false">This setup is to make sure all child data passes through, while
          adding the classes we want to apply. Remember to add
          :allow-for-loop="true" when doing v-for with the contents.</template>
        <component
          v-for="(child, i) in computedChildren"
          :is="child"
          :key="i"
          :class="{
            [computedChildColourClasses]: child.type != 'hr'
          }"
        />
      </div>
    </div>
  </div>
</template>
